import { mergeClasses } from '@fluentui/react-components';
import {
  IPBadge,
  IPButton,
  IPIcon,
  IPIconName,
  IPNav,
  IPNavCategory,
  IPNavCategoryItem,
  IPNavDivider,
  IPNavItem,
  IPNavSectionHeader,
  IPNavSubItem,
  IPNavSubItemGroup,
  isIPIconName,
} from 'baseComponents';
import { useGlobalTextStyles } from 'globalStyles';
import {
  FocusEvent as ReactFocusEvent,
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import NavHintCalloutV9, { INavHintCalloutHandle } from './NavHintCalloutV9';
import { navIconSize, navRailClass, navRowClass, useNavMainStyles } from './navMainStylesV9';
import { INavModelGroup, INavModelItem } from './navModel';
import { findNavHintRow, useNavHintHover } from './useNavHintHover';
import { useNavModel } from './useNavModel';

//
// The Fluent v9 rendering of the main menu. Which entries exist, where they sit and which one is current
// comes from useNavModel; this file draws it, as the full menu or as the icon rail. The pinned groups at the
// bottom open in a callout beside their header (NavHintCalloutV9), never in the menu.
//

/**
 * How one instance of the menu is drawn. 'full': labels, captions and children in place (the expanded rail,
 * the hover flyout, the mobile panel). 'icons': the compact rail; an entry without an icon shows a dot.
 */
export type NavPresentation = 'full' | 'icons';

export interface INavMainV9Props {
  presentation: NavPresentation;
}

const NavMainV9 = (props: INavMainV9Props) => {
  const styles = useNavMainStyles();
  const textStyles = useGlobalTextStyles();
  const compact = props.presentation === 'icons';
  // The flyout opens over the icon rail on the first pointer movement, so the pinned headers hint in the
  // full menu only.
  const footerHintable = props.presentation === 'full';
  const model = useNavModel();
  const hints = useNavHintHover();
  const calloutRef = useRef<INavHintCalloutHandle>(null);

  const openCategories = useMemo(
    () =>
      model.bodyGroups.reduce<string[]>((ids, group) => {
        group.items.forEach((item) => {
          if (item.children && item.children.length > 0 && item.isExpanded) ids.push(item.id);
        });

        return ids;
      }, []),
    [model.bodyGroups],
  );

  const renderIcon = (item: INavModelItem): JSX.Element | undefined => {
    const name: IPIconName | undefined = isIPIconName(item.iconName) ? item.iconName : compact ? 'Dot' : undefined;

    return name ? <IPIcon name={name} fontSize={navIconSize} /> : undefined;
  };

  // In the icon rail the label stays in the row for screen readers, out of sight.
  const renderLabel = (label: string): ReactNode =>
    compact ? <span className={styles.hiddenLabel}>{label}</span> : label;

  // the count beside the label; capped so it fits the row
  const renderBadge = (item: INavModelItem): ReactNode =>
    item.badgeCount !== undefined && item.badgeCount > 0 ? (
      <IPBadge size="small" appearance="filled" color="danger" className={compact ? styles.badgeCompact : styles.badge}>
        {item.badgeCount > 99 ? '99+' : item.badgeCount}
      </IPBadge>
    ) : null;

  const rowClassName = (item: INavModelItem) =>
    mergeClasses(navRowClass, styles.row, compact && styles.rowCompact, item.className);

  const renderBodyItem = (item: INavModelItem): JSX.Element => {
    if (item.children && item.children.length > 0) {
      return (
        <IPNavCategory key={item.id} value={item.id}>
          <IPNavCategoryItem className={rowClassName(item)} icon={renderIcon(item)}>
            {renderLabel(item.label)}
          </IPNavCategoryItem>
          <IPNavSubItemGroup>
            {item.children.map((child) => (
              <IPNavSubItem key={child.id} value={child.id} className={rowClassName(child)}>
                {renderLabel(child.label)}
              </IPNavSubItem>
            ))}
          </IPNavSubItemGroup>
        </IPNavCategory>
      );
    }

    return (
      <IPNavItem
        key={item.id}
        value={item.id}
        className={rowClassName(item)}
        icon={renderIcon(item)}
        title={compact ? item.label : undefined}
      >
        {renderLabel(item.label)}
        {renderBadge(item)}
      </IPNavItem>
    );
  };

  const renderGroupHeader = (group: INavModelGroup): JSX.Element | null => {
    if (!group.caption) {
      return null;
    }

    // The icon rail has no room for words: a rule marks the group, as high as the caption it stands for.
    if (compact) {
      return (
        <div className={styles.captionCompact}>
          <IPNavDivider className={styles.captionRule} />
        </div>
      );
    }

    const groupId = group.id;
    if (group.isCollapsible && groupId !== undefined) {
      return (
        <IPButton
          appearance="transparent"
          className={mergeClasses(styles.groupToggle, textStyles.subtle)}
          onClick={() => model.toggleGroup(groupId)}
        >
          {group.caption}
        </IPButton>
      );
    }

    return <IPNavSectionHeader className={styles.caption}>{group.caption}</IPNavSectionHeader>;
  };

  // A pinned header has no page; its pages are in the callout. A click opens the callout and never closes
  // it; a keyboard activation (a click without a pointer) also moves focus into it.
  const onHeaderClick = (ev: ReactMouseEvent<HTMLElement>): void => {
    const row = findNavHintRow(ev.currentTarget);
    if (row) {
      hints.open(row, { focus: ev.detail === 0 });
    }
  };

  const onBodySelect = (id: string): void => {
    model.selectItem(id);
    hints.close();
  };

  const onHintSelect = (id: string): void => {
    model.selectItem(id);
    hints.close();
  };

  const onRailKeyDown = (ev: ReactKeyboardEvent<HTMLElement>): void => {
    // Enter or ArrowRight on the open hint's header moves the keyboard into the callout.
    if (
      (ev.key === 'ArrowRight' || ev.key === 'Enter') &&
      hints.hint &&
      findNavHintRow(ev.target)?.id === hints.hint.id
    ) {
      ev.preventDefault();
      calloutRef.current?.focus();

      return;
    }

    hints.railHandlers.onKeyDown(ev);
  };

  const onRailFocus = (ev: ReactFocusEvent<HTMLElement>): void => {
    hints.railHandlers.onFocus(ev);
  };

  const openHintId = hints.hint?.id;
  const openHeader = openHintId ? model.footerItems.find((header) => header.id === openHintId) : undefined;

  //
  // Main render
  //
  return (
    <Fragment>
      <div
        className={mergeClasses(navRailClass, styles.rail)}
        onMouseLeave={hints.railHandlers.onMouseLeave}
        onMouseOver={hints.railHandlers.onMouseOver}
        onMouseMove={hints.railHandlers.onMouseMove}
        onFocus={onRailFocus}
        onKeyDown={onRailKeyDown}
      >
        <div className={mergeClasses(styles.scroller, compact && styles.scrollerCompact)}>
          <IPNav
            className={styles.nav}
            selectedValue={model.selectedId}
            openCategories={openCategories}
            onNavItemSelect={(_ev, data) => onBodySelect(String(data.value))}
            onNavCategoryItemToggle={(_ev, data) => {
              if (data.categoryValue !== undefined) model.toggleExpanded(String(data.categoryValue));
            }}
          >
            {model.bodyGroups.map((group, index) => (
              <Fragment key={group.id ?? `group-${index}`}>
                {renderGroupHeader(group)}
                {group.isExpanded && group.items.map((item) => renderBodyItem(item))}
              </Fragment>
            ))}
          </IPNav>
        </div>
        {model.footerItems.length > 0 && (
          <div className={styles.footer}>
            <IPNav className={styles.nav} selectedValue={model.selectedId}>
              {model.footerItems.map((header) => (
                <IPNavItem
                  key={header.id}
                  value={header.id}
                  data-nav-id={footerHintable ? header.id : undefined}
                  className={rowClassName(header)}
                  icon={renderIcon(header)}
                  // A header with a hint gives the browser no title; in the icon rail the flyout takes over.
                  title={compact ? header.label : undefined}
                  onClick={footerHintable ? onHeaderClick : undefined}
                >
                  {renderLabel(header.label)}
                </IPNavItem>
              ))}
            </IPNav>
          </div>
        )}
      </div>
      {/* A sibling of the rail, not a child: React routes a portal's events through its React parents, and
          the rail's hover handlers must not see the callout's rows. */}
      {hints.hint && openHeader && (
        <NavHintCalloutV9
          ref={calloutRef}
          target={hints.hint.element}
          header={openHeader}
          selectedKey={model.matchedId}
          focusOnOpen={hints.focusRequested}
          onSelect={onHintSelect}
          onDismiss={hints.close}
          onMouseEnter={hints.calloutHandlers.onMouseEnter}
          onMouseLeave={hints.calloutHandlers.onMouseLeave}
        />
      )}
    </Fragment>
  );
};

export default NavMainV9;
